import {
  above,
  below,
  type Check,
  has,
  hasSolid,
  inLine,
  onAxis,
  sameSpan,
  spanRatio,
  isMidpoint,
  labelled,
  noAmbiguousCorner,
  noCoincidentPoints,
  nothingMissing,
  onSegment,
  parses,
  sameLength,
  sameSpaceSpan,
  spaceAngle,
  spaceSpanRatio,
  withinBox,
} from './checks';

/**
 * The corpus: real problems, the script that draws them, and what has to be true of the result.
 *
 * ## Why the problem statement is stored
 *
 * Today the script beside it is written by hand. The point of the feature is that it will be
 * written by a model from that Vietnamese text, and when it is, the corpus is already the
 * benchmark: same problems, same checks, and a script a person has already accepted as the
 * baseline to beat. Storing only scripts would mean building the benchmark twice.
 *
 * ## Why the cases are chosen by *kind of figure*
 *
 * Because that is how the failures cluster. The language passed every statement-level test and
 * still could not draw a solid — plane geometry was complete and solids were missing four
 * things at once. A corpus of five triangles would have found none of it. Each case below is a
 * different shape of problem, and a new kind is worth more than a tenth variation of an old one.
 *
 * ## How to grow it
 *
 * When a figure comes out wrong, add the problem here with checks that fail, then fix the
 * language. The failure becomes a permanent statement about the product rather than a memory,
 * which is the only part of this that compounds.
 */

export interface GoldenCase {
  readonly name: string;
  /** The problem as a teacher would be handed it. The eventual input to a model. */
  readonly problem: string;
  /** What kind of figure this is. Coverage is counted over these, not over cases. */
  readonly kind: 'plane' | 'circle' | 'solid' | 'transform' | 'graph' | 'epure';
  readonly script: string;
  readonly checks: readonly Check[];
}

export const GOLDEN_CASES: readonly GoldenCase[] = [
  {
    name: 'cone cut by a plane',
    kind: 'solid',
    problem: [
      'Cho hình nón đỉnh S có đường sinh là a, góc giữa đường sinh và đáy là α.',
      'Một mặt phẳng hợp với đáy một góc 60° và cắt hình nón theo hai đường sinh SA và SB.',
      'Tính diện tích tam giác SAB và khoảng cách từ tâm đáy đến mặt phẳng này.',
    ].join('\n'),
    /*
     * Rebuilt on `cone`, and the reason is worth recording. This was the last figure in the
     * corpus placed by hand — an `oval` for the rim, two `point`s for the feet of the
     * silhouette, and every dash chosen by me looking at a rendering. It was also the only one
     * with a wrong dash: `OK` runs from the centre of the base to a point on `SM`, entirely
     * inside the cone, and I had drawn it solid. Nothing caught it, because nothing here knew
     * there was a body in the way.
     *
     * There is no hand-styling left below. The rim, the outline and every dashed line come
     * from the same classifier the prisms use, and `OK` is dashed because the cone covers it.
     */
    script: `
cone S O at 0 0 size 380 260
# Four things a reader needs, and they pull against each other. Measured across the rim:
#
#   A/B      outline gap   dihedral mark   |OM|     OM off vertical
#   300/20      12.2°           14°        0.77r          3°
#   285/45       3.7°           30°        0.50r         12°
#   320/280      8.3°           57°        0.62r         79°
#
# The last column is the one that took longest to see. A radius pointing at the *front* of the
# base projects almost straight down: tan(theta) = -0.433 puts it exactly vertical at 336.6°, so
# a chord bisected near the front collapses O, M and K into a vertical line and the triangle
# the question is about stops being a triangle.
#
# Bisecting at 300° instead, on the right of the base, sends OM nearly horizontal and fixes the
# other three at the same time. Both generators stay well inside the near arc, which runs from
# 255° round through the front to 75°.
A = rim O 320
B = rim O 280

SA = segment S A
SB = segment S B
AB = segment A B
style SA accent
style SB accent
style AB accent

M = midpoint A B
OM = segment O M
SM = segment S M

K = foot O S M
OK = segment O K
style OK accent

alpha = anglemark S A O
phi = anglemarkbig S M O

label S "S"
label O "O"
label A "A"
label B "B"
label M "M"
label K "K"
`,
    checks: [
      parses(),
      nothingMissing(),
      has('S', 'point'),
      has('SA', 'segment'),
      /*
       * A and B are no longer *checked* onto the rim, because they are now *defined* onto it:
       * `rim O 300` keeps the angle and works the point out from the cone every time the cone
       * changes. A check would be testing the constructor.
       *
       * Worth recording what the old one caught, since the capability went with it. It compared
       * A and B against a projected base ellipse computed a different way — two independent
       * routes to the same circle — and the first time it ran it found the *checker* wrong:
       * `onEllipse` divided by the radii without undoing the tilt, so two points exactly on a
       * rotated rim were reported 24% and 8% off it. That fix is still in `checks.ts`.
       */
      isMidpoint('M', 'A', 'B'),
      // K is the foot of the perpendicular from O, so it must actually be on SM.
      onSegment('K', 'S', 'M'),
      labelled('S', 'O', 'A', 'B', 'M', 'K'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'triangle with its circumcircle',
    kind: 'plane',
    problem: 'Cho tam giác ABC. Dựng đường tròn ngoại tiếp và xác định tâm O của nó.',
    script: `
A = point 0 0
B = point 160 0
C = point 50 120
AB = segment A B
BC = segment B C
CA = segment C A
O = circumcenter A B C
circ = circle3 A B C
label A "A"
label B "B"
label C "C"
label O "O"
`,
    checks: [
      parses(),
      nothingMissing(),
      has('O', 'point'),
      has('circ', 'circle'),
      // The definition of a circumcentre, asserted rather than assumed.
      sameLength('O', 'A', 'B', 1e-6),
      sameLength('O', 'B', 'C', 1e-6),
      labelled('A', 'B', 'C', 'O'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'parallelogram and its diagonals',
    kind: 'plane',
    problem:
      'Cho hình bình hành ABCD. Chứng minh hai đường chéo cắt nhau tại trung điểm mỗi đường.',
    script: `
A = point 0 0
B = point 150 0
C = point 200 90
D = point 50 90
AB = segment A B
BC = segment B C
CD = segment C D
DA = segment D A
AC = segment A C
BD = segment B D
I = intersect AC BD
label A "A"
label B "B"
label C "C"
label D "D"
label I "I"
`,
    checks: [
      parses(),
      nothingMissing(),
      has('I', 'point'),
      // The whole content of the problem, as two mechanical statements.
      isMidpoint('I', 'A', 'C'),
      isMidpoint('I', 'B', 'D'),
      labelled('A', 'B', 'C', 'D', 'I'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'tangents from a point outside a circle',
    kind: 'circle',
    problem: 'Cho đường tròn tâm O và điểm P nằm ngoài đường tròn. Dựng hai tiếp tuyến từ P.',
    script: `
O = point 0 0
E = point 90 0
c = circle O E
P = point 230 60
t = tangent c P
label O "O"
label P "P"
`,
    checks: [
      parses(),
      // The case that would quietly produce nothing if P were inside: a tangent from inside a
      // circle does not exist, and the kernel answers `null` rather than inventing one.
      nothingMissing(),
      has('c', 'circle'),
      labelled('O', 'P'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'a cubic and where it meets the x axis',
    kind: 'graph',
    problem:
      'Cho hàm số y = x^3 - 3x^2 + 2 có đồ thị (C). Khảo sát sự biến thiên và vẽ đồ thị của ' +
      'hàm số. Tìm toạ độ giao điểm của (C) với trục hoành.',
    /*
     * The kind the corpus had none of, though the rules carry more prose about it than about
     * anything else. `axes` and `graph` were added, tested statement by statement, described at
     * length in the instructions — and never once exercised on a whole problem here. What that
     * hid was not the curve, which is drawn well, but everything a problem says *about* it.
     *
     * A function problem almost always names points: the roots, the turning points, where two
     * curves meet. Those are given in graph coordinates, and until `plot` existed the language
     * had no way to place one — so a model wrote `point 1 0`, in drawing units, and the three
     * roots of this very cubic landed within 1.7 units of each other on a figure whose axes
     * reach 160. `noCoincidentPoints` below is what says they no longer do; run this case
     * against `point` instead of `plot` and it fails.
     */
    script: `
# Trên [-1;3] thì (b-a)/2 = 2 và |f| <= 2 trên đoạn ấy, nên đồ thị vừa khung nhìn.
axes 4
C = graph "x^3-3x^2+2" -1 3
label C "(C)"

# x^3-3x^2+2 = (x-1)(x^2-2x-2), nên đồ thị cắt trục hoành tại x = 1 và x = 1 ± √3.
P = plot -0.732 0
Q = plot 1 0
R = plot 2.732 0
label P "1-√3"
label Q "1"
label R "1+√3"
`,
    checks: [
      parses(),
      nothingMissing(),
      has('C', 'function-graph'),
      /*
       * The three roots are distinct points at their own coordinates. This is the assertion the
       * whole case exists for: it is exactly what failed when the roots were placed with
       * `point`, and it fails again the moment the two coordinate systems drift apart.
       */
      noCoincidentPoints(),
      labelled('P', 'Q', 'R', 'C'),
      // A root sits on the x axis, which is the y = 0 line of the graph's own coordinates.
      withinBox(),
    ],
  },

  {
    name: 'the two-view drawing of a segment',
    kind: 'epure',
    problem:
      'Trong hệ thống hai mặt phẳng hình chiếu vuông góc, cho đoạn thẳng AB với A có độ xa 40 ' +
      'và độ cao 130, B có độ xa 170 và độ cao 30. Vẽ đồ thức của AB, xác định vết đứng M và ' +
      'vết bằng N của đường thẳng AB, và dựng độ dài thật của đoạn AB.',
    /*
     * The audience that had nothing. Descriptive geometry is drawn on a two-view diagram and the
     * language had no notion of one — asked for an đồ thức, a model built it from `point` and
     * `segment`, which happens to work because the diagram is flat, and left the anchor points
     * it used for captions visible as stray dots beside the numbers.
     *
     * What this case pins is the convention, because a convention got silently wrong is the
     * failure here: the vertical projection above the axis at the *height*, the horizontal one
     * below at the *distance*, and the two sharing a correspondence line. Reverse the two and
     * every line of the diagram is still drawable and every statement it makes is false.
     *
     * The rest of the subject is not here and the reference says so. Traces, true length,
     * changing the plane of projection, the third view: each is a construction inside this
     * system, and this case is the frame they would be built on.
     */
    script: `
ground 460

epure A 60 40 130
epure B 300 170 30

# Vết đứng và vết bằng: hai đầu của đường thẳng trên đồ thức.
vtrace M A B
htrace N A B

MN1 = segment M1 N1
MN2 = segment M2 N2
style MN1 accent
style MN2 accent

# Độ dài thật của AB, dựng bằng tam giác vuông trên hình chiếu bằng.
truelength T A B
`,
    checks: [
      parses(),
      nothingMissing(),
      /*
       * The convention, as four statements that fail if it is ever flipped. A1 is above the
       * axis and A2 below it, and the two share a coordinate along the axis — which is what
       * makes them projections of one point rather than of two.
       */
      above('A1'),
      above('B1'),
      below('A2'),
      below('B2'),
      inLine('A1', 'A2'),
      inLine('B1', 'B2'),
      /*
       * What makes a trace a trace: a coordinate of zero, and the axis is where zero is. The
       * vertical trace has its *horizontal* projection on the line, and the horizontal trace
       * the other way round — reverse them and the diagram still draws and says the opposite.
       */
      onAxis('M2'),
      onAxis('N1'),
      inLine('M1', 'M2'),
      inLine('N1', 'N2'),
      labelled('A1', 'A2', 'B1', 'B2', 'M1', 'N2', 'T'),
      noCoincidentPoints(),
      withinBox(),
    ],
  },

  {
    name: 'reflection of a triangle in a line',
    kind: 'transform',
    problem: 'Cho tam giác ABC và đường thẳng d. Dựng ảnh của tam giác qua phép đối xứng trục d.',
    script: `
P = point -40 -60
Q = point 240 -60
d = line P Q
A = point 20 40
B = point 140 20
C = point 70 130
AB = segment A B
BC = segment B C
CA = segment C A
A2 = reflect A d
B2 = reflect B d
C2 = reflect C d
A2B2 = segment A2 B2
B2C2 = segment B2 C2
C2A2 = segment C2 A2
label A "A"
label B "B"
label C "C"
`,
    checks: [
      parses(),
      nothingMissing(),
      has('A2', 'point'),
      has('A2B2', 'segment'),
      /*
       * A reflection is an isometry, so each side of the image matches its original.
       *
       * The first version compared |AB| with |AC| — which asserts the triangle is isosceles,
       * something the problem never said. A check that passes only for the fixture is not a
       * check; it is a coincidence written down.
       */
      sameSpan('A', 'B', 'A2', 'B2'),
      sameSpan('B', 'C', 'B2', 'C2'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  /*
   * ================= From the exercise book =================
   *
   * `docs/bai-tap-hinh-hoc/200-bai-...pdf`, 203 problems, counted by figure:
   *
   *   68  chóp tứ giác   (pyramid on a quadrilateral base)
   *   59  lăng trụ       (prism)
   *   33  tứ diện        (tetrahedron)
   *   24  hình hộp       (box and cube)
   *   18  chóp tam giác
   *   18  mặt cầu
   *    5  hình trụ
   *    4  hình nón
   *
   * Worth reading twice: the cone that took a whole afternoon of this project is the *rarest*
   * figure in the book. A corpus assembled from whatever happened to be interesting would have
   * had the weights exactly backwards, which is the argument for taking the cases from a real
   * source rather than inventing them.
   *
   * ## What these cost before, and what that bought
   *
   * Each of the four below was first written by hand: eight projected coordinates worked out
   * on paper, twelve `segment` lines, and three `style … hidden` lines decided by looking at a
   * rendering. Around forty lines to arrive at the point where the *problem* begins, and the
   * dashes were right only because I checked them against a picture.
   *
   * They are now one statement each. That is what `prism` and `pyramid` are for, and the four
   * cases are the evidence they work: same problems, same checks, same figures.
   */
  {
    name: 'pyramid whose lateral edge is perpendicular to the base',
    kind: 'solid',
    problem:
      'Cho hình chóp S.ABCD có đáy ABCD là hình vuông cạnh a, SA vuông góc với mặt phẳng ' +
      'đáy và SA = a. Gọi M là trung điểm của BC. Tính góc giữa đường thẳng SM và mặt ' +
      'phẳng (ABCD).',
    /*
     * The commonest figure in Vietnamese solid geometry, and the one the corpus was missing.
     *
     * "SA vuông góc với mặt phẳng đáy" is `over A`, and nothing else is. Without it the apex
     * stands above the *centre* of the base, which is the default and is what a model writes
     * when every worked example it has seen omits the clause — measured on this very problem,
     * the angle at A came out 63.61° instead of 90°, and on its three-cornered twin 77.08°.
     *
     * The figure then contradicts itself in a way a reader can see without measuring anything:
     * `anglemark S A B` is drawn from the positions, so a mark the problem *requires* renders
     * as an ordinary acute arc sitting on a drawing whose caption says the angle is right. The
     * length goes too — with the apex over the centre, SA is longer than the height asked for.
     *
     * This case is first among the solids on purpose. `prompt.ts` takes its worked examples as
     * the first of each kind, so this is the pyramid a model is shown, and `over` arrives in the
     * example rather than only in a line of reference prose it can skim past.
     */
    script: `
# SA ⊥ (ABCD) là \`over A\`: đỉnh đứng thẳng trên A chứ không trên tâm đáy.
# Đáy là hình vuông cạnh a nên width = depth; SA = a nên số giữa cũng bằng a.
pyramid S . A B C D at 0 0 size 240 240 240 over A

M = midpoint B C
AM = segment A M
SM = segment S M
SA = segment S A
style AM accent
style SM accent

# Góc vuông tại A: SA ⊥ AB, và AB là cạnh đáy đang được vẽ nên dấu có hai cạnh để bám.
rA = anglemark S A B

# Góc đề hỏi, giữa SM và mặt đáy, là góc SMA — cả hai cạnh đều đã vẽ.
alpha = anglemark S M A
text alpha "α"
text SA "a"

label M "M"
`,
    checks: [
      parses(),
      nothingMissing(),
      has('S', 'point'),
      /*
       * The whole point of the case. Both are false without `over A`, and neither can be seen
       * on the page — a projection draws a right angle at whatever number it likes.
       */
      spaceAngle('S', 'A', 'B', 90),
      spaceAngle('S', 'A', 'D', 90),
      // `đáy ABCD là hình vuông cạnh a`, and `SA = a` is the same length again.
      sameSpaceSpan('A', 'B', 'B', 'C'),
      sameSpaceSpan('S', 'A', 'A', 'B'),
      /*
       * The diagonal of the whole solid, which is a√3 exactly when the base is that square and
       * SA is that height and perpendicular to it. One line that fails if any of the three slips.
       */
      spaceSpanRatio('S', 'C', 'A', 'B', Math.sqrt(3)),
      // The projection convention: true front face, halved depth, so the square reads as 2:1.
      spanRatio('A', 'B', 'B', 'C', 2),
      isMidpoint('M', 'B', 'C'),
      labelled('A', 'B', 'C', 'D', 'S', 'M'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'pyramid on a square base, with a midpoint of a lateral edge',
    kind: 'solid',
    problem:
      'Cho hình chóp S.ABCD có đáy ABCD là hình vuông cạnh a, tam giác SAB đều và ' +
      'góc SAD = 90°. J là trung điểm SD. Tính theo a thể tích khối tứ diện ACDJ ' +
      'và khoảng cách từ D đến mặt phẳng (ACJ).',
    script: `
# Đáy là hình vuông, nên width = depth. The size states the solid's true proportions;
# the projection is what turns the square into the parallelogram on the page.
pyramid S . A B C D at 0 0 size 240 210 240

J = midpoint S D
O = midpoint A C
AC = segment A C
AJ = segment A J
CJ = segment C J
style AJ accent
style CJ accent
hide O
label J "J"
`,
    checks: [
      parses(),
      nothingMissing(),
      has('S', 'point'),
      has('J', 'point'),
      /*
       * The base is a parallelogram — which is what a square becomes under projection, and the
       * property that survives it. Stated as "the diagonals share a midpoint", because that is
       * affine; "all four sides equal" is metric and would be false on the page for a perfectly
       * correct drawing.
       */
      isMidpoint('O', 'A', 'C'),
      isMidpoint('O', 'B', 'D'),
      isMidpoint('J', 'S', 'D'),
      // `đáy ABCD là hình vuông`: true front face, halved depth, so the square reads as 2:1.
      spanRatio('A', 'B', 'B', 'C', 2),
      // `pyramid` labels its own corners, so only `J` needs naming by hand.
      labelled('A', 'B', 'C', 'D', 'S', 'J'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'upright triangular prism, with a midpoint of a lateral edge',
    kind: 'solid',
    problem:
      "Cho lăng trụ đứng ABC.A'B'C' có đáy ABC là tam giác cân với AB = AC = a, " +
      "góc BAC = 120°, cạnh bên BB' = a. Gọi I là trung điểm của CC'. " +
      "Chứng minh tam giác AB'I vuông tại A.",
    script: `
# Đáy cân với AB = AC = a và góc BAC = 120°, said outright.
#
# It used to be written as \`size 450 220 150\`, with a comment explaining that a triangle
# inscribed in a width x depth ellipse has a 120° apex exactly when width = 3 x depth. That was
# true and it was a workaround: the base was not the triangle the problem describes, it was a
# scalene one that happened to have the right angle at A. \`angle\` says the thing itself, so
# AB and AC really are equal.
prism A B C . A2 B2 C2 at 0 0 size 250 250 250 angle A 120  # a = 250, so BB' = a too

I = midpoint C C2
AI = segment A I
B2I = segment B2 I
style AI accent
style B2I accent
label A2 "A'"
label B2 "B'"
label C2 "C'"
label I "I"
`,
    checks: [
      parses(),
      nothingMissing(),
      /*
       * A prism is the base translated. A translation survives projection intact, so every
       * lateral edge is the same length *on the page* — unlike almost everything else about a
       * solid, this one really is checkable in two dimensions.
       */
      sameSpan('A', 'A2', 'B', 'B2'),
      sameSpan('A', 'A2', 'C', 'C2'),
      // And the top face is congruent to the bottom, edge for edge.
      sameSpan('A', 'B', 'A2', 'B2'),
      sameSpan('B', 'C', 'B2', 'C2'),
      /*
       * The base is the triangle the problem describes, and neither of these can be seen on the
       * page: a projection is free to draw AB and AC at different lengths and the apex at some
       * angle other than 120°, and it does both. This case was drawn for a long time as a
       * scalene triangle that happened to have the right apex angle, and every check passed.
       */
      spaceAngle('B', 'A', 'C', 120),
      sameSpaceSpan('A', 'B', 'A', 'C'),
      isMidpoint('I', 'C', 'C2'),
      labelled('A', 'B', 'C', 'I'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'cube with points taken along two edges',
    kind: 'solid',
    problem:
      "Cho hình lập phương ABCD.A'B'C'D' có độ dài cạnh bằng a. Trên các cạnh AB và CD' " +
      'lấy lần lượt các điểm M, N sao cho BM = CN = x. Xác định vị trí điểm M sao cho ' +
      "khoảng cách giữa hai đường thẳng A'C và MN là nhỏ nhất.",
    script: `
# Hình lập phương: all three extents equal, or it is not the solid the problem names.
prism A B C D . A2 B2 C2 D2 at 0 0 size 210 210 210

O = midpoint A C
M = midpoint A B
N = midpoint C D2
MN = segment M N
A2C = segment A2 C
style MN accent
style A2C accent
hide O
label A2 "A'"
label B2 "B'"
label C2 "C'"
label D2 "D'"
label M "M"
label N "N"
`,
    checks: [
      parses(),
      nothingMissing(),
      // Both faces are parallelograms under projection, and the box is a translation of one.
      isMidpoint('O', 'A', 'C'),
      isMidpoint('O', 'B', 'D'),
      sameSpan('A', 'A2', 'C', 'C2'),
      sameSpan('A', 'B', 'A2', 'B2'),
      /*
       * `hình lập phương`: a cube and not merely a box. The vertical edge is unforeshortened
       * and the front edge is true, so the two measure the same on the page; the depth edge is
       * halved, so it measures half. Drawn as 220 x 210 x 140 the first of these was out by
       * ten units and the second by nothing like a factor of two — a box wearing the name of a
       * cube, which contradicts the sentence the figure exists to illustrate.
       */
      sameSpan('A', 'B', 'A', 'A2'),
      spanRatio('A', 'B', 'B', 'C', 2),
      isMidpoint('M', 'A', 'B'),
      labelled('A', 'B', 'C', 'M', 'N'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'cube inside its circumscribed sphere',
    kind: 'solid',
    problem:
      "Cho hình lập phương ABCD.A'B'C'D' có cạnh bằng a. Xác định tâm và tính bán kính mặt " +
      'cầu ngoại tiếp hình lập phương đó.',
    /*
     * The kind of solid the language could not draw at all, and the fourth commonest in the
     * book: eighteen problems about a mặt cầu, against nine about a cylinder and a cone put
     * together. `SolidKind` had carried `Sphere` and a tested mesh from the beginning — it was
     * only the written language that had no word for it, so a model asked for "bán kính mặt cầu
     * ngoại tiếp hình chóp S.ABC" drew the pyramid, labelled a point `I`, and stopped. The
     * figure was missing the object the question is about, and nothing said so.
     *
     * The cube is the case where the circumcentre is somewhere a script can put it: the centre
     * of a cube *is* the centre of its circumsphere, so both statements take the same `at 0 0`.
     * That is also the honest limit of this feature today — for "mặt cầu ngoại tiếp hình chóp"
     * the centre is a *derived* point, and `sphere` takes coordinates rather than a reference,
     * so that figure still cannot be written correctly. Worth knowing before this is called done.
     */
    script: `
# Cạnh a = 240. Đường chéo của khối lập phương là a√3 = 415,7, và nó là đường kính
# của mặt cầu ngoại tiếp — tâm mặt cầu trùng với tâm hình lập phương, nên cùng \`at 0 0\`.
prism A B C D . A2 B2 C2 D2 at 0 0 size 240 240 240
sphere O at 0 0 size 415.7

label O "O"
label A2 "A'"
label B2 "B'"
label C2 "C'"
label D2 "D'"
`,
    checks: [
      parses(),
      nothingMissing(),
      has('O', 'point'),
      /*
       * The cube is still a cube. Stated because the sphere shares its centre and its `at`, and
       * a mistake in either statement moves one of them without moving the other.
       */
      sameSpan('A', 'B', 'A', 'A2'),
      spanRatio('A', 'B', 'B', 'C', 2),
      sameSpaceSpan('A', 'B', 'B', 'C'),
      /*
       * The radius is the half-diagonal, which is what makes this sphere the circumscribed one
       * rather than a ball of some other size drawn round the cube. |AC'| = a√3 in space, and
       * the sphere's diameter was written as that same number.
       */
      spaceSpanRatio('A', 'C2', 'A', 'B', Math.sqrt(3)),
      labelled('A', 'B', 'C', 'D', 'O'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'oblique prism whose lateral edge leans over an edge midpoint',
    kind: 'solid',
    problem:
      "Cho lăng trụ ABC.A'B'C' có đáy ABC là tam giác đều cạnh a. Hình chiếu vuông góc của A' " +
      "lên mặt phẳng (ABC) trùng với trung điểm H của cạnh BC. Góc giữa cạnh bên AA' và mặt " +
      'đáy bằng 60°. Tính thể tích khối lăng trụ.',
    /*
     * The problem the language had no way to state, and the shape of what went wrong without it.
     *
     * `prism` translated its base straight up and `over` was refused on anything but a pyramid,
     * so a model asked for this placed all six corners by hand at coordinates it had projected
     * itself. The arithmetic was right and the figure was still broken: not one of the points
     * had a position in space, so no edge could be dashed and the solid drew as a flat tangle of
     * lines — and the angle mark it added measured 130.3° on the page under a label reading 60°.
     *
     * `over B C` says the thing the problem says. A' stands above the midpoint of BC, the whole
     * top ring moves with it so the solid is still a prism, and every line is classified in
     * space again.
     */
    script: `
# Đáy đều cạnh a: \`width\` là đường kính đường tròn ngoại tiếp, nên width = 1.155a.
# a = 200 → width = depth = 231. |AH| = a√3/2 = 173, và góc AA' với đáy là 60°,
# nên chiều cao = 173 × tan60° = 300.
prism A B C . A2 B2 C2 at 0 0 size 231 300 231 over B C

H = midpoint B C
AH = segment A H
A2H = segment A2 H
style AH accent
style A2H accent

# A'H ⊥ (ABC) là điều đề nói, và AH là một đường của đáy đang được vẽ.
rH = anglemark A2 H A
alpha = anglemarkbig A2 A H
text alpha "60°"

label A2 "A'"
label B2 "B'"
label C2 "C'"
label H "H"
`,
    checks: [
      parses(),
      nothingMissing(),
      // "đáy ABC là tam giác đều cạnh a" — all three sides, in space.
      sameSpaceSpan('A', 'B', 'B', 'C'),
      sameSpaceSpan('A', 'B', 'C', 'A'),
      /*
       * The two statements the figure exists to make, and neither can be read off the page.
       * A'H is perpendicular to the base, and the lateral edge meets the base at 60°.
       */
      spaceAngle('A2', 'H', 'A', 90),
      spaceAngle('A2', 'A', 'H', 60),
      /*
       * Still a prism. A translation survives projection, so the three lateral edges must
       * measure the same *on the page* — the one property of a leaning solid that a flat check
       * can see, and exactly the one a hand-placed figure gets wrong.
       */
      sameSpan('A', 'A2', 'B', 'B2'),
      sameSpan('A', 'A2', 'C', 'C2'),
      isMidpoint('H', 'B', 'C'),
      labelled('A', 'B', 'C', 'H'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },

  {
    name: 'the sphere circumscribing a pyramid, centred where the problem puts it',
    kind: 'solid',
    problem:
      'Cho hình chóp S.ABC có SA vuông góc với mặt phẳng (ABC), tam giác ABC vuông tại B, ' +
      'AB = a, BC = a√3 và SA = 2a. Xác định tâm và tính bán kính mặt cầu ngoại tiếp hình ' +
      'chóp S.ABC.',
    /*
     * The other half of the sphere, and the one that needed `at` to take a name.
     *
     * A cube's circumsphere shares the cube's own centre, so `at 0 0` twice draws it. Nothing
     * else obliges: here the centre is the midpoint of SC — because ∠SAC and ∠SBC are both
     * right, so both A and B see SC at a right angle and SC is a diameter — and that point is
     * *derived*. With coordinates only, this figure could not be written at all, and a model
     * asked for it drew the pyramid, labelled a point `I`, and left the sphere out.
     *
     * `at I` also makes it live: the origin is read from the parent when the figure evaluates,
     * so resizing the pyramid moves `I` and the sphere follows.
     */
    script: `
# a = 100. Đáy vuông tại B: AB = a = 100 (width), BC = a√3 = 173.2 (depth). SA = 2a = 200.
# SA ⊥ (ABC) là \`over A\`.
pyramid S . A B C at 0 0 size 100 200 173.2 right B over A

# ∠SAC = ∠SBC = 90° nên SC là đường kính, tâm là trung điểm SC, R = SC/2 = a√2.
I = midpoint S C
sphere at I size 283

SC = segment S C
style SC accent
AB = segment A B
BC = segment B C

rA = anglemark S A B
rB = anglemark A B C

label I "I"
text AB "a"
text BC "a√3"
`,
    checks: [
      parses(),
      nothingMissing(),
      hasSolid('sphere'),
      hasSolid('pyramid'),
      // The hypotheses, in space, where a projection cannot show them.
      spaceAngle('S', 'A', 'B', 90),
      spaceAngle('A', 'B', 'C', 90),
      spaceSpanRatio('B', 'C', 'A', 'B', Math.sqrt(3)),
      spaceSpanRatio('S', 'A', 'A', 'B', 2),
      /*
       * The claim the figure is *for*: I is the same distance from all four vertices, so the
       * sphere drawn round it really is the circumscribed one. This is the check that fails if
       * `at I` ever stops following the point it names.
       */
      sameSpaceSpan('I', 'S', 'I', 'A'),
      sameSpaceSpan('I', 'S', 'I', 'B'),
      sameSpaceSpan('I', 'S', 'I', 'C'),
      labelled('S', 'A', 'B', 'C', 'I'),
      noCoincidentPoints(),
      withinBox(),
    ],
  },

  {
    name: 'tetrahedron with the midpoints of two opposite edges',
    kind: 'solid',
    problem:
      'Cho tứ diện đều ABCD có độ dài các cạnh bằng a. Gọi M, N lần lượt là trung điểm ' +
      'của AB và CD. Tính khoảng cách giữa hai đường thẳng AB và CD.',
    script: `
# A tetrahedron is a pyramid on three corners — the apex is named D here rather than S,
# because that is how the problem names it.
# Tứ diện đều cạnh a: an equilateral base needs width = depth, and the height of a
# regular tetrahedron of side s is s * sqrt(2/3). Here s = 250 * sqrt(3)/2 = 216.5.
pyramid D . A B C at 0 0 size 250 176.8 250

M = midpoint A B
N = midpoint C D
MN = segment M N
style MN accent
label M "M"
label N "N"
`,
    checks: [
      parses(),
      nothingMissing(),
      isMidpoint('M', 'A', 'B'),
      isMidpoint('N', 'C', 'D'),
      // The common perpendicular joins the two midpoints, so both ends must be where they claim.
      onSegment('M', 'A', 'B'),
      onSegment('N', 'C', 'D'),
      labelled('A', 'B', 'C', 'D', 'M', 'N'),
      noCoincidentPoints(),
      noAmbiguousCorner(),
      withinBox(),
    ],
  },
];
